<template>
  <div class="table-responsive">
    <table class="table dre-gerencial-table mb-0">
      <thead>
        <tr>
          <th class="categoria-col">Categoria</th>
          <th
            v-for="period in periods"
            :key="period"
            class="text-right value-col"
            :class="{ 'current-period': isCurrentPeriod(period) }"
          >
            {{ formatPeriodHeader(period) }}
          </th>
          <th class="text-right value-col">Total</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(linha, index) in linhas">
          <!-- Section header + plan sub-rows -->
          <template v-if="linha.tipo === 'secao'">
            <tr :key="`secao-${index}`" class="row-secao">
              <td>
                <span
                  class="icon-circle mr-1"
                  :class="linha.sinal === 1 ? 'icon-positive' : 'icon-negative'"
                >
                  {{ linha.sinal === 1 ? "⊕" : "⊖" }}
                </span>
                <strong>{{ linha.descricao }}</strong>
              </td>
              <td
                v-for="period in periods"
                :key="period"
                class="text-right"
                :class="{ 'current-period': isCurrentPeriod(period) }"
              >
                <strong :class="linha.sinal === 1 ? 'text-success' : 'text-danger'">
                  {{ formatCurrency(getBalance(linha.balances, period)) }}
                </strong>
              </td>
              <td class="text-right">
                <strong :class="linha.sinal === 1 ? 'text-success' : 'text-danger'">
                  {{ formatCurrency(linha.total) }}
                </strong>
              </td>
            </tr>
            <tr
              v-for="plano in linha.planos"
              :key="`plano-${plano.id}`"
              class="row-plano"
            >
              <td class="plano-descricao" :class="plano.tipo === 2 ? 'border-left-positive' : 'border-left-negative'">
                {{ plano.descricao }}
              </td>
              <td
                v-for="period in periods"
                :key="period"
                class="text-right"
                :class="{ 'current-period': isCurrentPeriod(period) }"
              >
                {{ formatCurrency(getBalance(plano.balances, period)) }}
              </td>
              <td class="text-right">
                {{ formatCurrency(planoTotal(plano.balances)) }}
              </td>
            </tr>
          </template>

          <!-- Subtotal row -->
          <tr
            v-else-if="linha.tipo === 'subtotal'"
            :key="`subtotal-${index}`"
            class="row-subtotal"
          >
            <td>
              <span class="icon-subtotal mr-1">=</span>
              <strong>{{ linha.descricao }}</strong>
            </td>
            <td
              v-for="period in periods"
              :key="period"
              class="text-right"
              :class="{ 'current-period': isCurrentPeriod(period) }"
            >
              <strong :class="valueClass(getBalance(linha.balances, period))">
                {{ formatCurrency(getBalance(linha.balances, period)) }}
              </strong>
            </td>
            <td class="text-right">
              <strong :class="valueClass(linha.total)">
                {{ formatCurrency(linha.total) }}
              </strong>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "RelatorioDREGerencialTable",
  props: {
    linhas: {
      type: Array,
      required: true,
    },
    periods: {
      type: Array,
      required: true,
    },
  },
  methods: {
    isCurrentPeriod(period) {
      const now = moment();
      // Mensal: MM/YYYY
      if (/^\d{2}\/\d{4}$/.test(period)) {
        return period === now.format("MM/YYYY");
      }
      // Trimestral: T1-T4/YYYY
      if (/^T\d\/\d{4}$/.test(period)) {
        const [q, year] = period.split("/");
        const quarter = parseInt(q.replace("T", ""));
        return parseInt(year) === now.year() && now.quarter() === quarter;
      }
      // Semestral: S1-S2/YYYY
      if (/^S\d\/\d{4}$/.test(period)) {
        const [s, year] = period.split("/");
        const semester = parseInt(s.replace("S", ""));
        const currentSemester = now.month() < 6 ? 1 : 2;
        return parseInt(year) === now.year() && currentSemester === semester;
      }
      // Anual: YYYY
      if (/^\d{4}$/.test(period)) {
        return parseInt(period) === now.year();
      }
      return false;
    },
    formatPeriodHeader(period) {
      // Mensal: MM/YYYY
      if (/^\d{2}\/\d{4}$/.test(period)) {
        const [month, year] = period.split("/");
        const m = moment(`${year}-${month}-01`).locale("pt-BR");
        const abbr = m.format("MMM");
        const capitalized = abbr.charAt(0).toUpperCase() + abbr.slice(1).replace(".", "");
        return `${capitalized} de ${year}`;
      }
      // Trimestral: T1/2026
      if (/^T\d\/\d{4}$/.test(period)) {
        const [q, year] = period.split("/");
        return `${q}° Trim. ${year}`;
      }
      // Semestral: S1/2026
      if (/^S\d\/\d{4}$/.test(period)) {
        const [s, year] = period.split("/");
        return `${s.replace("S", "")}° Sem. ${year}`;
      }
      // Anual: 2026
      if (/^\d{4}$/.test(period)) {
        return period;
      }
      return period;
    },
    getBalance(balances, period) {
      const found = (balances || []).find((b) => b.period === period);
      return found ? found.balance : 0;
    },
    planoTotal(balances) {
      return (balances || []).reduce((sum, b) => sum + (b.balance || 0), 0);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }).format(value || 0);
    },
    valueClass(value) {
      if (value > 0) return "text-success";
      if (value < 0) return "text-danger";
      return "";
    },
  },
};
</script>

<style scoped>
.dre-gerencial-table th,
.dre-gerencial-table td {
  font-size: 0.93rem;
  padding: 0.4rem 0.75rem;
  vertical-align: middle;
  border-color: #e9ecef;
}

.categoria-col {
  min-width: 280px;
}

.value-col {
  min-width: 120px;
  white-space: nowrap;
}

.current-period {
  background-color: #fffde7 !important;
}

.row-secao td {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6 !important;
}

.row-plano td {
  background-color: #ffffff;
}

.plano-descricao {
  padding-left: 2rem !important;
  border-left-width: 3px !important;
  border-left-style: solid !important;
}

.border-left-positive {
  border-left-color: #1bc5bd !important;
}

.border-left-negative {
  border-left-color: #f64e60 !important;
}

.row-subtotal td {
  background-color: #f0f4f8;
  border-top: 2px solid #dee2e6 !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.icon-circle {
  font-size: 1.15rem;
}

.icon-positive {
  color: #1bc5bd;
}

.icon-negative {
  color: #f64e60;
}

.icon-subtotal {
  color: #ff9800;
  font-size: 1.15rem;
  font-weight: bold;
}
</style>
